import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PushPin, Trash, NotePencil } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    getAccessToken,
    addToCallPlan,
    updateCallPlan,
    removeFromCallPlan,
} from "@/lib/api";
import { CALL_PLAN_RESULTS } from "@/lib/callPlan";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
};

export const CallPlanBtn = ({ property, className = "", size = "w-7 h-7" }) => {
    const nav = useNavigate();
    const initial = Boolean(property.isInCallPlan ?? property.isCallPlan);
    const [added, setAdded] = useState(initial);
    const [busy, setBusy] = useState(false);
    const [loginPrompt, setLoginPrompt] = useState(false);

    useEffect(() => {
        setAdded(Boolean(property.isInCallPlan ?? property.isCallPlan));
    }, [property.isInCallPlan, property.isCallPlan]);

    const onClick = async (e) => {
        stop(e);

        if (!getAccessToken()) {
            setLoginPrompt(true);
            return;
        }

        if (busy) return;

        setBusy(true);
        try {
            if (added) {
                await removeFromCallPlan(property.idProperty);
                setAdded(false);
                toast.success("Zəng planından çıxarıldı");
            } else {
                await addToCallPlan(property.idProperty);
                setAdded(true);
                toast.success("Zəng planına əlavə olundu");
            }
        } catch {
            toast.error("Əməliyyat mümkün olmadı");
        } finally {
            setBusy(false);
        }
    };

    const confirmLogin = () => {
        setLoginPrompt(false);
        nav("/login");
    };

    return (
        <>
            <button
                type="button"
                onClick={onClick}
                onMouseDown={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                disabled={busy}
                data-testid={`call-plan-btn-${property.idProperty}`}
                title={added ? "Zəng planından çıxar" : "Zəng planına əlavə et"}
                aria-label="Zəng planı"
                className={`${size} shrink-0 rounded-full flex items-center justify-center shadow-md transition disabled:opacity-60 ${added
                        ? "bg-emerald-600 text-white hover:bg-emerald-700"
                        : "bg-white text-slate-700 hover:bg-slate-100"
                    } ${className}`}
            >
                <PushPin size={14} weight={added ? "fill" : "bold"} />
            </button>

            <AlertDialog open={loginPrompt} onOpenChange={setLoginPrompt}>
                <AlertDialogContent
                    data-testid="call-plan-login-dialog"
                    onClick={(e) => e.stopPropagation()}
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle>Giriş tələb olunur</AlertDialogTitle>
                        <AlertDialogDescription>
                            Zəng planına əlavə etmək üçün daxil olun.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="call-plan-login-cancel">
                            Ləğv et
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="call-plan-login-confirm"
                            onClick={confirmLogin}
                        >
                            Daxil ol
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export const CallPlanStatus = ({ property, compact = false }) => {
    const qc = useQueryClient();
    const [value, setValue] = useState(property.callPlan?.resultTypeId ?? 1);
    const [note, setNote] = useState(property.callPlan?.note ?? "");
    const [draft, setDraft] = useState(property.callPlan?.note ?? "");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [noteOpen, setNoteOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setValue(property.callPlan?.resultTypeId ?? 1);
    }, [property.callPlan?.resultTypeId]);

    useEffect(() => {
        const n = property.callPlan?.note ?? "";
        setNote(n);
        setDraft(n);
    }, [property.callPlan?.note]);

    const current = CALL_PLAN_RESULTS.find((r) => r.id === value);
    const hasNote = note.trim().length > 0;

    const onChange = async (v) => {
        const next = Number(v);
        const prev = value;
        setValue(next);

        try {
            await updateCallPlan(property.idProperty, {
                resultTypeId: next,
                resultNote: note,
            });
            toast.success("Status yeniləndi");
            qc.invalidateQueries({ queryKey: ["listing"] });
        } catch {
            setValue(prev);
            toast.error("Statusu dəyişmək mümkün olmadı");
        }
    };

    const openNote = (e) => {
        stop(e);
        setDraft(note);
        setNoteOpen(true);
    };

    const onSaveNote = async () => {
        if (saving) return;
        const next = draft.trim();
        setSaving(true);
        try {
            await updateCallPlan(property.idProperty, {
                resultTypeId: value,
                resultNote: next,
            });
            setNote(next);
            toast.success("Plan qeydi yadda saxlanıldı");
            qc.invalidateQueries({ queryKey: ["listing"] });
            setNoteOpen(false);
        } catch {
            toast.error("Plan qeydini yadda saxlamaq mümkün olmadı");
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async () => {
        try {
            await removeFromCallPlan(property.idProperty);
            toast.success("Zəng planından silindi");
            qc.invalidateQueries({ queryKey: ["listing"] });
        } catch {
            toast.error("Silmək mümkün olmadı");
        }
        setConfirmOpen(false);
    };

    return (
        <div
            className={`${compact ? "inline-flex flex-col" : "flex w-full flex-col"} gap-1.5 min-w-0`}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <div className="flex w-full items-center gap-1.5 min-w-0">
                <Select value={String(value)} onValueChange={onChange}>
                    <SelectTrigger
                        data-testid={`call-plan-status-${property.idProperty}`}
                        className={`h-7 min-h-0 min-w-0 px-2 py-0 gap-1 ${compact ? "w-[150px]" : "flex-1"} rounded-md text-[11px] font-semibold border ${current?.cls || ""}`}
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {CALL_PLAN_RESULTS.map((r) => (
                            <SelectItem key={r.id} value={String(r.id)}>
                                {r.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <button
                    type="button"
                    onClick={openNote}
                    data-testid={`call-plan-note-${property.idProperty}`}
                    title={hasNote ? "Plan qeydini redaktə et" : "Plan qeydi əlavə et"}
                    aria-label="Plan qeydi"
                    className={`w-7 h-7 shrink-0 rounded-full border flex items-center justify-center transition-colors ${hasNote
                            ? "border-amber-400 bg-amber-50 text-amber-600 hover:bg-amber-100"
                            : "border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted"
                        }`}
                >
                    <NotePencil size={12} weight={hasNote ? "fill" : "regular"} />
                </button>

                <button
                    type="button"
                    onClick={(e) => {
                        stop(e);
                        setConfirmOpen(true);
                    }}
                    data-testid={`call-plan-remove-${property.idProperty}`}
                    title="Plandan sil"
                    aria-label="Plandan sil"
                    className="w-7 h-7 shrink-0 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive hover:bg-destructive/5 transition-colors"
                >
                    <Trash size={12} />
                </button>
            </div>

            {hasNote && (
                <button
                    type="button"
                    onClick={openNote}
                    data-testid={`call-plan-note-preview-${property.idProperty}`}
                    title={note}
                    className="flex w-full items-center gap-1.5 min-w-0 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-left text-[11px] leading-4 text-amber-900 hover:bg-amber-100 transition-colors"
                >
                    <NotePencil size={12} weight="fill" className="shrink-0 text-amber-600" />
                    <span className="truncate">{note}</span>
                </button>
            )}

            <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
                <DialogContent
                    data-testid="call-plan-note-dialog"
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                >
                    <DialogHeader>
                        <DialogTitle>Plan qeydi · #{property.idProperty}</DialogTitle>
                    </DialogHeader>
                    <Textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={5}
                        maxLength={1000}
                        placeholder="Zəng nəticəsi haqqında qeydinizi yazın..."
                        data-testid="call-plan-note-input"
                    />
                    <DialogFooter>
                        <button
                            type="button"
                            onClick={() => setNoteOpen(false)}
                            className="h-9 px-4 rounded-md border border-border text-sm hover:bg-muted transition-colors"
                        >
                            Ləğv et
                        </button>
                        <button
                            type="button"
                            onClick={onSaveNote}
                            disabled={saving}
                            data-testid="call-plan-note-save"
                            className="h-9 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-60"
                        >
                            Yadda saxla
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent
                    data-testid="call-plan-remove-dialog"
                    onClick={(e) => e.stopPropagation()}
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle>Zəng planından silinsin?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Bu elan zəng planınızdan çıxarılacaq.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Xeyr</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete}>Sil</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};